import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Plus, Trash2 } from 'lucide-react'
import { createReview } from '../../services/api'

interface CreateReviewModalProps {
  onClose: () => void
}

export default function CreateReviewModal({ onClose }: CreateReviewModalProps) {
  const queryClient = useQueryClient()
  const [documentId, setDocumentId] = useState('')
  const [reviewers, setReviewers] = useState<string[]>([''])
  const [reviewType, setReviewType] = useState<'sequential' | 'parallel'>('parallel')
  const [error, setError] = useState('')

  const createMutation = useMutation({
    mutationFn: () =>
      createReview({
        document_id: documentId,
        reviewers: reviewers.filter(Boolean),
        review_type: reviewType,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] })
      onClose()
    },
    // Bug #6275: reviews/handler.py serialises a rejection as {"error": "..."}
    // (shared/responses.py::error()) — the axios response interceptor never
    // copies that onto the thrown error's own .message, so an
    // `e instanceof Error ? e.message` check only ever shows axios's generic
    // "Request failed with status code 400". Read response.data.error instead,
    // matching the pattern already used at ShareModal.tsx / MoveDocumentModal.tsx
    // / ProjectAIAnalysisPanel.tsx.
    onError: (err: any) =>
      setError(err?.response?.data?.error || 'Failed to create review request. Please try again.'),
  })

  const addReviewer = () => setReviewers([...reviewers, ''])
  const removeReviewer = (i: number) => setReviewers(reviewers.filter((_, idx) => idx !== i))
  const updateReviewer = (i: number, value: string) => {
    const updated = [...reviewers]
    updated[i] = value
    setReviewers(updated)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Request Review</h3>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Document ID</label>
            <input
              value={documentId}
              onChange={(e) => setDocumentId(e.target.value)}
              className="w-full px-3 py-2 light-input text-sm outline-none"
              placeholder="Enter document ID"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Review Type</label>
            <div className="flex gap-2">
              {(['parallel', 'sequential'] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => setReviewType(type)}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors capitalize ${
                    reviewType === type
                      ? 'bg-iflow-50 border-iflow-300 text-iflow-700'
                      : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reviewers</label>
            <div className="space-y-2">
              {reviewers.map((reviewer, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    value={reviewer}
                    onChange={(e) => updateReviewer(i, e.target.value)}
                    className="flex-1 px-3 py-2 light-input text-sm outline-none"
                    placeholder="reviewer@example.com"
                  />
                  {reviewers.length > 1 && (
                    <button onClick={() => removeReviewer(i)} className="p-2 text-gray-500 hover:text-red-600">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={addReviewer}
              className="flex items-center gap-1 mt-2 text-sm text-iflow-600 hover:text-iflow-700"
            >
              <Plus size={14} /> Add reviewer
            </button>
            <p className="mt-2 text-xs text-gray-500">
              Enter the exact email of someone who already has access to this project —
              invite them via the project&apos;s Access tab first if they don&apos;t.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={() => createMutation.mutate()}
            disabled={!documentId.trim() || !reviewers.some(Boolean) || createMutation.isPending}
            className="px-4 py-2 text-sm text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50"
          >
            {createMutation.isPending ? 'Creating...' : 'Request Review'}
          </button>
        </div>
      </div>
    </div>
  )
}
